/**
 * « Les affinités » — the sorting script the generation panel plays.
 *
 * The panel computes nothing: it plays back one slot of a generation that has
 * already happened. Everything here is read off the real plan, so every lit
 * wire is a true statement about the recipe it points at. Nothing is invented
 * for the show — a winner that only matches two facts shows two.
 */
import type { RecipeIndexEntry } from '../../services/unifiedRecipeService';
import type { AllergenKey } from '../../data/allergens';
import type { DietType } from '../../types/recipe';
import { pantryHits } from '../../services/fanSuggestionsService';
import { dietKindOf, type DietKind } from '../weekly/v2/dietKind';
import { DAY_PROFILE, dayOf, revealOrder, rowOf } from '../weekly/v2/constants';
import { fitsMealType } from './fitsMealType';

/** The one hard rule the household chip names. Every eligible recipe keeps it. */
export type HouseholdRule =
  | { source: 'allergen'; value: AllergenKey }
  | { source: 'ingredient'; value: string }
  | { source: 'diet'; value: DietType };

/**
 * What Du Chef knows about the home, one chip each. Facts carry values, never
 * copy: the panel translates at render, so a language switch is honoured.
 */
export type AffinityFact =
  | { key: 'household'; rule: HouseholdRule }
  | { key: 'pantry'; items: string[] }
  | { key: 'time'; di: number; minutes: number }
  | { key: 'history' }
  | { key: 'diet'; kind: DietKind; count: number };

export interface AffinityCandidate {
  recipeId: string;
  title: string;
  /** Catalogue translations; the panel shows the one for the app language. */
  translatedTitles?: { en?: string; fr?: string };
  image?: string;
  /** Indexes into `facts` of the facts this recipe honours, ascending. */
  hits: number[];
  /** 0..1, the gauge length. Never shown as a number. */
  score: number;
}

export interface AffinityScript {
  /** The witness slot: the header's « mercredi soir » and « gardé pour mercredi ». */
  slot: { di: number; mi: number };
  /** At most four, in display order. */
  facts: AffinityFact[];
  /**
   * Three candidates, the last one being the recipe the plan really put in
   * `slot`. Empty when the library is too small to have runners-up — the panel
   * then lights the facts in turn instead.
   */
  candidates: AffinityCandidate[];
  /** Distinct recipes the generator weighed across the whole week. */
  reviewed: number;
  /** Slots the plan filled — the footer's « 7 gardées ». */
  kept: number;
}

export interface AffinityContext {
  /** The first hard rule in force, if any (allergen, excluded ingredient, diet). */
  household?: HouseholdRule | null;
  /** Up to two pantry display names, soonest to expire first. */
  pantry?: string[];
  /** Recipe ids planned the week before the one being generated. */
  previousWeekIds?: string[];
  /** The week's soft distribution targets. */
  targets?: { fish: number; veg: number; meat: number };
  /** The same hard exclusion the generator applied. */
  exclude?: (r: RecipeIndexEntry) => boolean;
  kindOf?: (r: RecipeIndexEntry) => DietKind;
  /** Injected for tests; picks among equally good runners-up. */
  random?: () => number;
}

export const MAX_FACTS = 4;
const RUNNERS_UP = 2;

/** 0.35 + 0.6 × share of facts honoured — a gauge that is never empty. */
export function affinityScore(hits: number, facts: number): number {
  if (facts === 0) return 0.35;
  return 0.35 + 0.6 * (hits / facts);
}

/** The week's leading distribution target: the kind asked for most. */
function leadingKind(targets: { fish: number; veg: number; meat: number }): { kind: DietKind; count: number } {
  const [kind, count] = (Object.entries(targets) as [DietKind, number][])
    .sort((a, b) => b[1] - a[1])[0];
  return { kind, count };
}

/**
 * The facts for one slot, in priority order, capped at four. `time` always
 * exists; when fewer than two facts are available (a brand-new account), the
 * week's diet target joins it — it always exists too.
 */
export function affinityFacts(sk: number, ctx: AffinityContext): AffinityFact[] {
  const facts: AffinityFact[] = [];
  if (ctx.household) facts.push({ key: 'household', rule: ctx.household });
  const pantry = (ctx.pantry ?? []).filter(Boolean).slice(0, 2);
  if (pantry.length > 0) facts.push({ key: 'pantry', items: pantry });
  const di = dayOf(sk);
  facts.push({ key: 'time', di, minutes: DAY_PROFILE[di].maxT });
  if ((ctx.previousWeekIds ?? []).length > 0) facts.push({ key: 'history' });
  if (facts.length < 2) {
    facts.push({ key: 'diet', ...leadingKind(ctx.targets ?? { fish: 2, veg: 2, meat: 1 }) });
  }
  return facts.slice(0, MAX_FACTS);
}

/** Which of `facts` this recipe honours — each one a real test, never a guess. */
export function factHits(recipe: RecipeIndexEntry, facts: AffinityFact[], ctx: AffinityContext): number[] {
  const kindOf = ctx.kindOf ?? dietKindOf;
  const previous = new Set(ctx.previousWeekIds ?? []);
  const hits: number[] = [];
  facts.forEach((fact, i) => {
    let hit = false;
    switch (fact.key) {
      case 'household':
        hit = !(ctx.exclude && ctx.exclude(recipe));
        break;
      case 'pantry':
        hit = pantryHits(recipe, fact.items) > 0;
        break;
      case 'time':
        hit = recipe.readyInMinutes > 0 && recipe.readyInMinutes <= fact.minutes;
        break;
      case 'history':
        hit = !previous.has(recipe.id);
        break;
      case 'diet':
        hit = kindOf(recipe) === fact.kind;
        break;
    }
    if (hit) hits.push(i);
  });
  return hits;
}

const toCandidate = (r: RecipeIndexEntry, hits: number[], facts: number): AffinityCandidate => ({
  recipeId: r.id,
  title: r.title,
  ...(r.translatedTitles ? { translatedTitles: r.translatedTitles } : {}),
  image: r.image,
  hits,
  score: affinityScore(hits.length, facts),
});

/**
 * Two runners-up that honour fewer facts than the winner — ideally one weak,
 * one close — so the story reads « not this, closer, this one ». Photos are
 * preferred, since the card is mostly its picture. Falls back to ties when
 * nothing honours fewer (a winner that only matches the household rule, which
 * every eligible recipe matches too).
 */
function pickRunnersUp(
  pool: { r: RecipeIndexEntry; hits: number[] }[],
  winnerHits: number,
  random: () => number,
): { r: RecipeIndexEntry; hits: number[] }[] {
  const shuffled = pool
    .map((c) => ({ c, k: random() }))
    .sort((a, b) => (Number(!!b.c.r.image) - Number(!!a.c.r.image)) || a.k - b.k)
    .map(({ c }) => c);

  const weaker = shuffled.filter((c) => c.hits.length < winnerHits);
  const source = weaker.length >= RUNNERS_UP ? weaker : shuffled.filter((c) => c.hits.length <= winnerHits);
  if (source.length < RUNNERS_UP) return [];

  // The closest one second, the weakest distinct count first.
  const close = [...source].sort((a, b) => b.hits.length - a.hits.length)[0];
  const rest = source.filter((c) => c !== close);
  const weak = rest.find((c) => c.hits.length < close.hits.length) ?? rest[0];
  return [weak, close];
}

export interface ExplainInput {
  /** slotKey → recipe id, as produced by buildGenerationPlan. */
  plan: Record<number, string>;
  recipeIndex: RecipeIndexEntry[];
  /** From buildGenerationPlan: distinct recipes weighed for the whole week. */
  reviewed: number;
}

/**
 * Build the script for the witness slot: of every slot the plan filled, the
 * one whose winner honours the most facts, the earliest revealed on a tie.
 * The winner is the recipe really placed there; the runners-up are eligible
 * recipes for that slot the plan did not use.
 *
 * Returns null only when the plan filled nothing (there is nothing to show).
 */
export function explainGenerationPlan(input: ExplainInput, ctx: AffinityContext = {}): AffinityScript | null {
  const { plan, recipeIndex, reviewed } = input;
  const filled = revealOrder(Object.keys(plan).map(Number));
  if (filled.length === 0) return null;

  const byId = new Map(recipeIndex.map((r) => [r.id, r]));
  const random = ctx.random ?? Math.random;

  let best: { sk: number; facts: AffinityFact[]; winner: RecipeIndexEntry; hits: number[] } | null = null;
  for (const sk of filled) {
    const winner = byId.get(plan[sk]);
    if (!winner) continue;
    const facts = affinityFacts(sk, ctx);
    const hits = factHits(winner, facts, ctx);
    const ratio = hits.length / facts.length;
    const bestRatio = best ? best.hits.length / best.facts.length : -1;
    if (ratio > bestRatio) best = { sk, facts, winner, hits };
  }
  if (!best) return null;

  const { sk, facts, winner, hits } = best;
  const used = new Set(Object.values(plan));
  const pool = recipeIndex
    .filter((r) => !used.has(r.id) && fitsMealType(r, rowOf(sk)) && !(ctx.exclude && ctx.exclude(r)))
    .map((r) => ({ r, hits: factHits(r, facts, ctx) }));

  const runnersUp = pickRunnersUp(pool, hits.length, random);
  const candidates = runnersUp.length === RUNNERS_UP
    ? [
      ...runnersUp.map((c) => toCandidate(c.r, c.hits, facts.length)),
      toCandidate(winner, hits, facts.length),
    ]
    : [];

  return {
    slot: { di: dayOf(sk), mi: rowOf(sk) },
    facts,
    candidates,
    reviewed: Math.max(reviewed, candidates.length),
    kept: filled.length,
  };
}

/**
 * The household chip names one hard rule — the first in force. Allergies come
 * first: they are the rule a cook most wants to see honoured.
 */
export function firstHouseholdRule(input: {
  allergens?: AllergenKey[];
  customAllergens?: string[];
  dislikedIngredients?: string[];
  dietaryRestrictions?: DietType[];
}): HouseholdRule | null {
  const firstText = (list?: string[]) => (list ?? []).map((s) => s.trim()).find(Boolean);
  if (input.allergens?.length) return { source: 'allergen', value: input.allergens[0] };
  const custom = firstText(input.customAllergens);
  if (custom) return { source: 'ingredient', value: custom };
  const disliked = firstText(input.dislikedIngredients);
  if (disliked) return { source: 'ingredient', value: disliked };
  if (input.dietaryRestrictions?.length) return { source: 'diet', value: input.dietaryRestrictions[0] };
  return null;
}

interface PantryLike {
  name: string;
  expiryDate?: { year: number; month: number; day: number };
}

/**
 * Up to two pantry foods to use up, soonest to expire first. Only dated items
 * that have not expired yet — « use it up » is a promise about the future.
 * `displayName` reduces a raw line to the food it names and drops staples
 * (returning null), so « 2 cups flour » never becomes the fact.
 */
export function soonestPantryNames(
  items: PantryLike[],
  todayIso: string,
  displayName: (raw: string) => string | null,
): string[] {
  const iso = (d: { year: number; month: number; day: number }) =>
    `${d.year}-${String(d.month).padStart(2, '0')}-${String(d.day).padStart(2, '0')}`;
  const dated = items
    .filter((i) => i.expiryDate && iso(i.expiryDate) >= todayIso)
    .sort((a, b) => iso(a.expiryDate!).localeCompare(iso(b.expiryDate!)));
  const names: string[] = [];
  for (const item of dated) {
    const name = displayName(item.name);
    if (name && !names.some((n) => n.toLowerCase() === name.toLowerCase())) names.push(name);
    if (names.length === 2) break;
  }
  return names;
}
